import QRCode from "qrcode";

export interface PrintSheetInput {
  candidateName: string;
  testId: string;
  url: string;
  home?: string | null;
  position?: string | null;
  /** e.g. "Mon, Tue, Wed, Thu, Fri, 08:00-18:00 (UK time)" */
  windowText: string;
  /** e.g. "14 days (until 29/10/2026)" */
  linkText: string;
  timeLimitMinutes: number;
}

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Opens a clean, printable one-page sheet the manager can hand to a candidate
 * or pin up: candidate name, Test ID, a large QR code, the link, when the test
 * can be taken and how long the link lasts. Printed via the browser dialog.
 */
export async function printCandidateSheet(input: PrintSheetInput): Promise<void> {
  const w = window.open("", "_blank", "width=800,height=1000");
  if (!w) {
    alert("Please allow pop-ups for this site to print the candidate sheet.");
    return;
  }
  let qr: string;
  try {
    qr = await QRCode.toDataURL(input.url, { width: 480, margin: 1 });
  } catch {
    w.document.body.textContent = "The print sheet could not be prepared. Close this window and try Print sheet again.";
    return;
  }
  const meta = [input.home, input.position].filter((x): x is string => !!x).map(esc).join(" · ");
  w.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(input.testId)} - assessment</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Open Sans', Arial, sans-serif; color: #173C69; margin: 0; padding: 40px; }
  .wrap { max-width: 640px; margin: 0 auto; text-align: center; }
  .brand { font-size: 22px; font-weight: 800; letter-spacing: .3px; }
  .sub { color: #5b7596; font-size: 13px; margin-top: 2px; }
  h1 { font-size: 26px; margin: 28px 0 4px; }
  .meta { color: #5b7596; font-size: 14px; }
  .qr { margin: 24px auto 8px; width: 300px; height: 300px; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
  .qr img { width: 100%; height: 100%; }
  .tid { font-family: monospace; font-size: 20px; font-weight: 700; letter-spacing: 1px; margin-top: 4px; }
  .url { font-size: 13px; color: #173C69; word-break: break-all; margin: 10px auto; max-width: 520px; }
  .facts { text-align: left; margin: 24px auto 0; max-width: 520px; border-top: 2px solid #173C69; padding-top: 16px; }
  .facts div { margin: 8px 0; font-size: 14px; }
  .facts strong { display: inline-block; min-width: 150px; color: #5b7596; font-weight: 600; }
  .note { margin-top: 20px; font-size: 13px; color: #5b7596; background: #f3f6fb; border-radius: 10px; padding: 14px 16px; text-align: left; }
  @media print { body { padding: 20px; } .noprint { display: none; } }
  .btn { margin-top: 24px; background: #173C69; color: #fff; border: 0; padding: 10px 22px; border-radius: 10px; font-size: 15px; cursor: pointer; }
</style></head><body><div class="wrap">
  <div class="brand">liaise</div>
  <div class="sub">Recruitment Assessment</div>
  <h1>${esc(input.candidateName)}</h1>
  ${meta ? `<div class="meta">${meta}</div>` : ""}
  <div class="qr"><img src="${qr}" alt="QR code"></div>
  <div class="tid">${esc(input.testId)}</div>
  <div class="url">${esc(input.url)}</div>
  <div class="facts">
    <div><strong>How to start</strong> Scan the code above with a phone camera, or type the web address into a browser. No login needed.</div>
    <div><strong>When it can be taken</strong> ${esc(input.windowText)}</div>
    <div><strong>Link valid for</strong> ${esc(input.linkText)}. The link expiry does not extend the allowed assessment hours.</div>
    <div><strong>Time to complete</strong> ${input.timeLimitMinutes > 0 ? `${input.timeLimitMinutes} minutes once started (answers submit automatically when time is up)` : "No time limit"}</div>
  </div>
  <div class="note">The candidate will be asked to agree to a short privacy notice and confirm the work is their own before starting. Please let them read the questions without help. When they finish, they will be asked to let you know.</div>
  <button class="btn noprint" onclick="window.print()">Print</button>
</div>
<script>setTimeout(function(){ try { window.print(); } catch(e){} }, 400);</script>
</body></html>`);
  w.document.close();
}

/** Availability must be explicit; missing data never means unrestricted access. */
export function windowText(win: { enabled: boolean; description: string } | undefined): string {
  if (!win?.description?.trim()) return "Schedule unavailable. Ask recruitment to confirm the allowed days and hours.";
  if (!win.enabled) return "Schedule restrictions are disabled in Admin. Ask recruitment to confirm availability.";
  return `Only ${win.description}. Candidates who start before closing can finish within their time limit.`;
}
